import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { 
  FileText, Send, Download, Mail, Loader2, AlertTriangle, CheckCircle, Clock, ArrowLeft, Trash2, Eye, Edit3, Banknote, FileX
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("fr-FR");
};

export default function EngagementsResiliations() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [resiliations, setResiliations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [letterContent, setLetterContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingLrar, setSendingLrar] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showLrarModal, setShowLrarModal] = useState(false);
  const [showEmailConfirmModal, setShowEmailConfirmModal] = useState(false);
  const [emailDestinataire, setEmailDestinataire] = useState("");
  const [laposteStatus, setLaposteStatus] = useState(null);
  const [previewMode, setPreviewMode] = useState(true); // true = formatted preview, false = edit mode

  useEffect(() => {
    fetchResiliations();
    fetchLaposteStatus();
  }, []);

  useEffect(() => {
    const id = searchParams.get("id");
    if (id && resiliations.length > 0) {
      const found = resiliations.find(r => r.id === id);
      if (found) {
        setSelected(found);
        setLetterContent(found.lettre_contenu || "");
      }
    }
  }, [searchParams, resiliations]);

  const fetchResiliations = async () => {
    try {
      const res = await axios.get(`${API}/api/engagements/resiliations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResiliations(res.data.resiliations || []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLaposteStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/engagements/laposte/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLaposteStatus(res.data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSaveLetter = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/engagements/resiliations/${selected.id}`, {
        lettre_contenu: letterContent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lettre enregistrée");
      fetchResiliations();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleSendLrar = async () => {
    if (!selected) return;
    setSendingLrar(true);
    try {
      const res = await axios.post(`${API}/api/engagements/resiliations/${selected.id}/send-lrar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`LRAR envoyée ! Suivi: ${res.data.numero_suivi}`);
      setShowLrarModal(false);
      fetchResiliations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingLrar(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = 210;
      const pageHeight = 297;
      const marginLeft = 20;
      const marginRight = 20;
      const marginTop = 20;
      const contentWidth = pageWidth - marginLeft - marginRight;
      
      doc.setFont('helvetica', 'normal');
      const fontSize = 10;
      const lineHeight = 5;
      
      let y = marginTop;
      
      // Split content into lines
      const lines = letterContent.split('\n');
      
      // Find where signature block starts (after salutations)
      let signatureStartIndex = -1;
      for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim().toLowerCase();
        if (trimmed.includes('salutations') || trimmed.includes('agréer')) {
          signatureStartIndex = i + 1;
          break;
        }
      }
      
      doc.setFontSize(fontSize);
      
      lines.forEach((line, index) => {
        // Page break check
        if (y > pageHeight - 25) {
          doc.addPage();
          y = marginTop;
        }
        
        const trimmed = line.trim();
        
        // Empty line = small spacing
        if (trimmed === '') {
          y += lineHeight * 0.6;
          return;
        }
        
        // Detect if line is indented (for recipient/date - right side)
        const isIndented = line.startsWith('                    ') || line.match(/^\s{15,}/);
        
        // Detect object line
        const isObject = trimmed.startsWith('Objet :') || trimmed.startsWith('Objet:');
        
        // Signature block: everything after "salutations" line (except the salutations line itself)
        const isInSignatureBlock = signatureStartIndex > 0 && index > signatureStartIndex;
        
        if (isObject) {
          doc.setFont('helvetica', 'bold');
        } else if (isInSignatureBlock) {
          doc.setFont('helvetica', 'bold');
        } else {
          doc.setFont('helvetica', 'normal');
        }
        
        if (isIndented) {
          // Right-aligned content (recipient, date)
          const wrapped = doc.splitTextToSize(trimmed, 80);
          wrapped.forEach(wl => {
            const textWidth = doc.getTextWidth(wl);
            doc.text(wl, pageWidth - marginRight - textWidth, y);
            y += lineHeight;
          });
        } else if (isInSignatureBlock) {
          // Signature block - ALL lines right aligned
          const textWidth = doc.getTextWidth(trimmed);
          doc.text(trimmed, pageWidth - marginRight - textWidth, y);
          y += lineHeight;
        } else {
          // Normal left-aligned content
          const wrapped = doc.splitTextToSize(trimmed, contentWidth);
          wrapped.forEach(wl => {
            doc.text(wl, marginLeft, y);
            y += lineHeight;
          });
        }
      });

      // Dynamic filename
      const letterTypeName = selected?.type_lettre === "remboursement_anticipe" 
        ? "demande_remboursement_anticipe" 
        : "lettre_resiliation";
      const contractName = (selected?.contrat_nom || "contrat").replace(/[^a-zA-Z0-9]/g, '_');
      doc.save(`${letterTypeName}_${contractName}.pdf`);
      toast.success("PDF téléchargé");
    } catch (error) {
      console.error("PDF error:", error);
      toast.error("Erreur lors de la génération du PDF");
    }
  };

  const handleSendEmail = async () => {
    if (!selected) return;
    
    // First, get the contract to check if email exists
    try {
      const contractRes = await axios.get(
        `${API}/api/engagements/contrats/${selected.contrat_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const email = contractRes.data.email_prestataire;
      if (!email) {
        toast.error("Aucun email de prestataire trouvé pour ce contrat");
        toast.info("Ajoutez l'email dans les détails du contrat avant d'envoyer");
        return;
      }
      
      // Show confirmation modal with email
      setEmailDestinataire(email);
      setShowEmailConfirmModal(true);
    } catch (error) {
      toast.error("Erreur lors de la récupération du contrat");
    }
  };

  const confirmSendEmail = async () => {
    if (!selected) return;
    
    setSendingEmail(true);
    setShowEmailConfirmModal(false);
    
    try {
      const res = await axios.post(
        `${API}/api/engagements/resiliations/${selected.id}/send-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message || "Email envoyé avec succès");
      fetchResiliations();
    } catch (error) {
      const detail = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(detail);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDeleteResiliation = async () => {
    if (!selected) return;
    if (!window.confirm(selected?.type_lettre === "remboursement_anticipe" 
      ? "Supprimer cette demande de remboursement anticipé ?" 
      : "Supprimer cette lettre de résiliation ?")) return;
    
    try {
      await axios.delete(`${API}/api/engagements/resiliations/${selected.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Lettre supprimée");
      setSelected(null);
      setLetterContent("");
      fetchResiliations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-64 space-y-4">
        <div className="relative">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
          <FileText className="w-6 h-6 text-blue-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="text-center">
          <p className="text-slate-600 font-medium">Chargement des résiliations...</p>
          <p className="text-slate-400 text-sm mt-1">Récupération de vos lettres en cours</p>
        </div>
      </div>
    );
  }

  // Determine if we have any early repayment letters
  const hasEarlyRepaymentLetters = resiliations.some(r => r.type_lettre === "remboursement_anticipe");
  const pageTitle = hasEarlyRepaymentLetters ? "Lettres & Demandes" : "Résiliations";
  const pageSubtitle = hasEarlyRepaymentLetters 
    ? "Gérez vos lettres de résiliation et demandes de remboursement" 
    : "Gérez vos lettres de résiliation";

  // Get letter type label
  const getLetterTypeLabel = (letter) => {
    if (letter?.type_lettre === "remboursement_anticipe") {
      return "Demande de remboursement anticipé";
    }
    return "Lettre de résiliation";
  };

  const getLetterTypeShort = (letter) => {
    if (letter?.type_lettre === "remboursement_anticipe") {
      return "DEMANDE DE REMBOURSEMENT ANTICIPÉ";
    }
    return "LETTRE DE RÉSILIATION";
  };

  return (
    <div className="p-6 space-y-6" data-testid="engagements-resiliations">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>
          <p className="text-slate-500">{pageSubtitle}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Lettres générées</CardTitle>
          </CardHeader>
          <CardContent>
            {resiliations.length === 0 ? (
              <p className="text-slate-500 text-center py-8">
                Aucune lettre générée.<br />
                <Button variant="link" onClick={() => navigate("/engagements/contrats")}>
                  Voir les contrats
                </Button>
              </p>
            ) : (
              <div className="space-y-2">
                {resiliations.map(r => (
                  <div
                    key={r.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selected?.id === r.id ? "bg-blue-50 border-blue-200" : "hover:bg-slate-50"
                    }`}
                    onClick={() => { setSelected(r); setLetterContent(r.lettre_contenu || ""); setPreviewMode(true); }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {/* Visual indicator icon based on letter type */}
                        <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
                          r.type_lettre === "remboursement_anticipe" 
                            ? "bg-emerald-100 text-emerald-600" 
                            : "bg-orange-100 text-orange-600"
                        }`}>
                          {r.type_lettre === "remboursement_anticipe" 
                            ? <Banknote className="w-5 h-5" /> 
                            : <FileX className="w-5 h-5" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 truncate">{r.contrat_nom || "Contrat"}</p>
                          <p className={`text-xs truncate ${
                            r.type_lettre === "remboursement_anticipe" 
                              ? "text-emerald-600 font-medium" 
                              : "text-orange-600"
                          }`}>
                            {r.type_lettre === "remboursement_anticipe" ? "Remboursement anticipé" : "Résiliation"}
                          </p>
                        </div>
                      </div>
                      <Badge className={
                        r.statut_envoi === "confirme" ? "bg-green-100 text-green-700" :
                        r.statut_envoi === "envoye_lrar" ? "bg-blue-100 text-blue-700" :
                        r.statut_envoi === "envoye_email" ? "bg-purple-100 text-purple-700" :
                        "bg-slate-100 text-slate-600"
                      }>
                        {r.statut_envoi === "confirme" ? "Confirmé" :
                         r.statut_envoi === "envoye_lrar" ? "LRAR envoyée" :
                         r.statut_envoi === "envoye_email" ? "Email envoyé" :
                         "Brouillon"}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 pl-12">
                      {formatDate(r.created_at)}
                      {r.numero_suivi_laposte && ` • Suivi: ${r.numero_suivi_laposte}`}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: Editor */}
        <Card className="lg:col-span-2">
          {selected ? (
            <>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{getLetterTypeLabel(selected)}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">{selected.contrat_nom}</p>
                  </div>
                  <div className="flex gap-2">
                    {selected.statut_envoi === "brouillon" && (
                      <Button variant="outline" size="sm" onClick={handleDeleteResiliation} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="w-4 h-4 mr-1" />
                        Supprimer
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={handleDownloadPdf}>
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleSendEmail}
                      disabled={sendingEmail}
                    >
                      {sendingEmail ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4 mr-1" />
                      )}
                      Email
                    </Button>
                    <Button size="sm" onClick={() => setShowLrarModal(true)} disabled={!laposteStatus?.success}>
                      <Send className="w-4 h-4 mr-1" />
                      LRAR
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status alerts */}
                {selected.statut_envoi === "envoye_lrar" && (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="text-green-600" />
                    <AlertDescription className="text-green-700">
                      LRAR envoyée le {formatDate(selected.date_envoi)}
                      {selected.numero_suivi_laposte && (
                        <span className="block mt-1 font-mono text-sm">
                          N° de suivi: {selected.numero_suivi_laposte}
                        </span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                {selected.statut_envoi === "envoye_email" && (
                  <Alert className="bg-blue-50 border-blue-200">
                    <Mail className="text-blue-600" />
                    <AlertDescription className="text-blue-700">
                      Email envoyé le {formatDate(selected.date_envoi)}
                      {selected.email_destinataire && (
                        <span className="block mt-1">
                          À: {selected.email_destinataire}
                        </span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}

                {!laposteStatus?.success && (
                  <Alert>
                    <AlertTriangle />
                    <AlertDescription>
                      L'envoi LRAR n'est pas disponible.{" "}
                      <Button variant="link" className="p-0 h-auto" onClick={() => navigate("/engagements/parametres")}>
                        Configurer La Poste
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Letter editor/preview toggle and content */}
                <div className="w-full space-y-3">
                  {/* Toggle buttons */}
                  {selected.statut_envoi === "brouillon" && (
                    <div className="flex items-center gap-2 pb-2 border-b">
                      <Button 
                        variant={previewMode ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setPreviewMode(true)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Aperçu
                      </Button>
                      <Button 
                        variant={!previewMode ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setPreviewMode(false)}
                      >
                        <Edit3 className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                    </div>
                  )}

                  {/* Content display */}
                  {!previewMode && selected.statut_envoi === "brouillon" ? (
                    // Edit mode - Textarea
                    <div className="overflow-x-auto">
                      <Textarea
                        value={letterContent}
                        onChange={e => setLetterContent(e.target.value)}
                        rows={28}
                        className="font-mono text-sm w-full"
                        placeholder="Contenu de la lettre..."
                      />
                    </div>
                  ) : (
                    // Preview mode - Formatted letter view (like a real letter)
                    <div className="bg-white border-2 border-slate-200 rounded-lg shadow-lg mx-auto overflow-hidden" style={{ maxWidth: '750px' }}>
                      {/* Letter header bar */}
                      <div className="bg-gradient-to-r from-slate-100 to-slate-50 px-6 py-3 border-b flex items-center justify-between">
                        <span className="text-xs text-slate-500 font-medium uppercase tracking-wide">
                          {selected.type_lettre === "remboursement_anticipe" ? "Demande de remboursement anticipé" : "Lettre de résiliation"}
                        </span>
                        <Badge variant={selected.statut_envoi === "brouillon" ? "secondary" : selected.statut_envoi === "envoye" ? "default" : "outline"}>
                          {selected.statut_envoi === "brouillon" ? "Brouillon" : selected.statut_envoi === "envoye" ? "Envoyé" : "Confirmé"}
                        </Badge>
                      </div>
                      
                      {/* Letter content - Paper style with proper formatting */}
                      <div 
                        className="p-8 md:p-12 bg-white min-h-[600px]"
                        style={{ 
                          fontFamily: 'Georgia, "Times New Roman", Times, serif',
                          lineHeight: '1.6',
                          fontSize: '13px'
                        }}
                      >
                        {(() => {
                          const lines = letterContent.split('\n');
                          // Find signature start (after salutations)
                          let signatureStartIndex = -1;
                          for (let i = 0; i < lines.length; i++) {
                            const trimmed = lines[i].trim().toLowerCase();
                            if (trimmed.includes('salutations') || trimmed.includes('agréer')) {
                              signatureStartIndex = i + 1;
                              break;
                            }
                          }
                          
                          return lines.map((line, index) => {
                            const trimmed = line.trim();
                            if (!trimmed) {
                              return <div key={index} className="h-3" />;
                            }
                            
                            // Detect indented lines (recipient/date)
                            const isIndented = line.startsWith('                    ') || line.match(/^\s{15,}/);
                            
                            // Detect object line
                            const isObject = trimmed.startsWith('Objet :') || trimmed.startsWith('Objet:');
                            
                            // Signature block
                            const isInSignatureBlock = signatureStartIndex > 0 && index > signatureStartIndex;
                            
                            if (isIndented) {
                              return (
                                <div key={index} className="text-right text-slate-800">
                                  {trimmed}
                                </div>
                              );
                            } else if (isInSignatureBlock) {
                              return (
                                <div key={index} className="text-right font-semibold text-slate-800">
                                  {trimmed}
                                </div>
                              );
                            } else if (isObject) {
                              return (
                                <div key={index} className="font-bold text-slate-900 mt-2">
                                  {trimmed}
                                </div>
                              );
                            } else {
                              return (
                                <div key={index} className="text-slate-800">
                                  {trimmed}
                                </div>
                              );
                            }
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </div>

                {selected.statut_envoi === "brouillon" && (
                  <div className="flex justify-end">
                    <Button onClick={handleSaveLetter} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Enregistrer les modifications
                    </Button>
                  </div>
                )}
              </CardContent>
            </>
          ) : (
            <CardContent className="flex items-center justify-center h-96 text-slate-500">
              <div className="text-center">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p>Sélectionnez une lettre pour la visualiser</p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* LRAR Modal */}
      <Dialog open={showLrarModal} onOpenChange={setShowLrarModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Envoyer en LRAR via La Poste</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Destinataire</p>
                <p className="font-medium">{selected?.contrat_nom || "-"}</p>
              </div>
              <div>
                <p className="text-slate-500">Tarif estimé</p>
                <p className="font-medium">{laposteStatus?.estimated_price || "4,90"} €</p>
              </div>
              <div>
                <p className="text-slate-500">Délai de livraison</p>
                <p className="font-medium">{laposteStatus?.delivery_delay || "J+1 ouvrable"}</p>
              </div>
              <div>
                <p className="text-slate-500">Environnement</p>
                <Badge variant="outline">{laposteStatus?.environment || "sandbox"}</Badge>
              </div>
            </div>
            
            <Alert className="bg-blue-50 border-blue-200">
              <AlertTriangle className="text-blue-600" />
              <AlertDescription className="text-blue-700">
                Une fois envoyée, la lettre ne pourra plus être modifiée.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLrarModal(false)}>Annuler</Button>
            <Button onClick={handleSendLrar} disabled={sendingLrar}>
              {sendingLrar ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Confirmer l'envoi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Confirmation Modal */}
      <Dialog open={showEmailConfirmModal} onOpenChange={setShowEmailConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Confirmer l'envoi par email
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-slate-600">
              Vous êtes sur le point d'envoyer {selected?.type_lettre === "remboursement_anticipe" ? "la demande de remboursement anticipé" : "la lettre de résiliation"} par email à :
            </p>
            
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-500">Destinataire</p>
              <p className="text-lg font-semibold text-blue-700">{emailDestinataire}</p>
            </div>

            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Contrat concerné</p>
              <p className="font-medium">{selected?.contrat_nom}</p>
              <p className="text-sm text-slate-500 mt-1">Prestataire: {selected?.prestataire}</p>
            </div>

            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="text-amber-600" />
              <AlertDescription className="text-amber-700">
                L'email sera envoyé immédiatement. Cette action ne peut pas être annulée.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEmailConfirmModal(false)}>
              Annuler
            </Button>
            <Button onClick={confirmSendEmail} disabled={sendingEmail}>
              {sendingEmail ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Oui, envoyer l'email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
